import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../service/auth.service';
import { UserActivityApiService } from 'src/app/shared/services/customer-api/user-activity-api.service';
import { resolveScoreTypeKey } from 'src/app/shared/utils/score-type-details';

/** 7-day window ending on the alert date, as `YYYY-MM-DD--YYYY-MM-DD`. */
const alertWindow = (alertDate?: string): string | null => {
  if (!alertDate) return null;
  const end = new Date(alertDate);
  if (isNaN(end.getTime())) return null;
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - 7);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return `${fmt(start)}--${fmt(end)}`;
};

/**
 * Normalizes inbound links into the canonical, shareable cockpit URL in a single
 * rewrite: routes `redirect=true` links, logs + strips `referrer`, and translates
 * alert links (`focused_score`/`alert_date`) into the internal `score`/`time`
 * vocabulary. Downstream the app only ever reads `score`/`time`.
 */
export const redirectGuard: CanActivateFn = (next, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const userActivityApi = inject(UserActivityApiService);

  const params = state.root.queryParams;
  const isRedirected = params.redirect;
  const isReferrer = params.referrer;
  if (isRedirected) {
    if (params.referrer) {
      const source = params.referrer;
      const url = window.location.href;
      const payload = {};
      Object.keys(params).forEach((item) => {
        if (item !== 'referrer' && item !== 'redirect') {
          payload[item] = params[item];
        }
      });
      userActivityApi.postReferrer(source, url, payload).subscribe();
    }
    let path = '/';
    let type = null;
    if (params.type) {
      if (params.type === 'search_query') {
        path = '/cockpit/news/query/chart';
      } else if (params.type === 'search_query_group') {
        path = '/cockpit/news/query/chart';
      } else if (params.type === 'portfolio') {
        path = '/cockpit/news/portfolio/chart';
      } else {
        path = '/cockpit/news/market/chart';
        type = params.type;
      }
    }
    router.navigate([path], {
      queryParams: {
        news: params.news ? params.news : null,
        type: type,
        id: params.id ? params.id : null,
        redirect: null,
        referrer: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    return false;
  }

  // Alert emails link with `focused_score` (+ `alert_id`/`alert_date`); the chart
  // focuses via `score`/`time`. Translate once, here: `bull_bear`→`bb`,
  // `alert_date`→a 7-day window, and drop the alert-only params.
  // Gate on `alert_id`: only genuine alert arrivals translate. In-app navigations
  // that carry `focused_score` (e.g. the default score, for refresh bookkeeping)
  // must NOT be turned into a focus — the portfolio route re-runs this guard on
  // every navigation (`runGuardsAndResolvers: 'always'`), so an ungated translate
  // would wrongly flip on score-focus mode mid-session.
  const requested = params.alert_id
    ? params.focused_score || params.score
    : null;
  const focusId = requested ? resolveScoreTypeKey(requested) : null;

  if (!isReferrer && !focusId) {
    return true;
  }

  if (params.jwt) {
    auth.token = params.jwt;
  }
  if (isReferrer) {
    const payload = {};
    Object.keys(params).forEach((item) => {
      if (!['referrer', 'redirect', 'jwt'].includes(item)) {
        payload[item] = params[item];
      }
    });
    userActivityApi
      .postReferrer(params.referrer, window.location.href, payload)
      .subscribe();
  }

  router.navigate([state.url.split('?')[0]], {
    queryParams: {
      ...params,
      referrer: null,
      ...(focusId && {
        score: focusId,
        focused_score: null,
        alert_id: null,
        alert_date: null,
        time: alertWindow(params.alert_date) ?? params.time,
      }),
    },
    queryParamsHandling: 'merge',
    replaceUrl: true,
  });
  return false;
};
